import { computed, ref, watchEffect, toValue } from "vue";
import { useShopZoneList, type ShopZone } from "@src/api";
import { ShopZone as ShopZoneModel } from "@src/model";
import { normalize, deepClone } from "@src/utils";

export type ShopZoneWithChildren = ShopZone & {
  path?: string;
  children?: ShopZoneWithChildren[];
  isDepartment?: boolean;
  parentId?: string;
};

export const useProductZoning = () => {
  const { data: shopZoneList } = useShopZoneList();

  const selectedShopZone = ref<ShopZoneWithChildren | null>(null);
  const normalizedZones = ref<{
    byId: Record<string, ShopZoneWithChildren>;
    allIds: string[];
  }>({ byId: {}, allIds: [] });

  watchEffect(() => {
    if (!shopZoneList.value) return;

    const normalized = normalize<ShopZoneWithChildren, "zone_id">(
      deepClone(toValue(shopZoneList).shop_zones),
      "zone_id",
    );

    for (let i = 0; i < shopZoneList.value.shop_zones.length; i++) {
      const current = normalized.map[shopZoneList.value.shop_zones[i].zone_id];
      const hierarchy = current.hierarchy.split(".");
      const prev = normalized.map[hierarchy.at(-2)];

      if (prev) {
        current.parentId = prev.zone_id;
        current.path = ShopZoneModel.getPath(current.hierarchy, (id) => normalized.map[id]?.title);
      } else {
        continue;
      }

      if (prev.children) {
        prev.children.push(current);
      } else {
        prev.children = [current];
      }
    }

    normalizedZones.value = { byId: normalized.map, allIds: normalized.ids };
    selectedShopZone.value = Object.values(normalized.map)
      .filter((item) => !item.parentId)
      .at(0);
  });

  const shopDepartmentList = computed(() => {
    return Object.values(normalizedZones.value.byId).filter((item) => !item.parentId);
  });

  return {
    shopZoneList,
    selectedShopZone,
    shopDepartmentList,
    normalizedZones,
  };
};
